import { useState, useEffect } from 'react'
import { Plus, Trash2, Edit2, FileText, X, BarChart3, ChevronRight, ChevronDown, ChevronUp, Activity, Send, Copy, Mail, MessageSquare, CheckCircle, Target, Info, Wand2, Sparkles, CreditCard, Printer, Link, Eye   } from 'lucide-react'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import { useSearchParams } from 'react-router-dom'
import { format, differenceInYears } from 'date-fns'
import { fr } from 'date-fns/locale'
import toast from 'react-hot-toast'
import api from '../../../services/api'
import ShareModal from './ShareModal'

const NOTE_TYPES = {
    anamnese: { label: 'Bilan Initial', color: 'bg-purple-100 text-purple-700' },
    follow_up: { label: 'Suivi', color: 'bg-blue-100 text-blue-700' },
    free: { label: 'Note libre', color: 'bg-gray-100 text-gray-700' }
}

const MEAL_TIMES = {
    breakfast: '🌅 Petit-déjeuner',
    lunch: '☀️ Déjeuner',
    snack: '🍎 Goûter',
    dinner: '🌙 Dîner'
}

export default function PatientNotesTab({ patientId, patient, autoOpenSendForm = false }) {
    const[searchParams, setSearchParams] = useSearchParams()
    const [notes, setNotes] = useState([])
    const[loading, setLoading] = useState(true)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [templates, setTemplates] = useState([])          
    const [formTemplates, setFormTemplates] = useState([])  

    //  Mesures pour le calculateur
    const[latestMeasures, setLatestMeasures] = useState({ weight: null, height: null })
    const [activityLevel, setActivityLevel] = useState(1.375) // 1.2 Sédentaire, 1.375 Léger, 1.55 Modéré...

    const [editingNote, setEditingNote] = useState(null)
    const[formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        note_type: 'follow_up',
        content: '',
        diet_recall: null,
        form_data: null,
    })

    const [isSendModalOpen, setIsSendModalOpen] = useState(false)
    const[selectedFormId, setSelectedFormId] = useState('')
    const [generatedLink, setGeneratedLink] = useState('')
    const [generatedReqId, setGeneratedReqId] = useState(null)
    const[sendVia, setSendVia] = useState({ email: true, sms: false })
    const [isSending, setIsSending] = useState(false)

    const [shareItem, setShareItem] = useState(null)

    const [showDietRecall, setShowDietRecall] = useState(false)
    const[recallItems, setRecallItems] = useState([])
    const [suggestions, setSuggestions] = useState([])
    const[showSuggestions, setShowSuggestions] = useState(false)
    const [expandedRecallId, setExpandedRecallId] = useState(null)
    const [newItem, setNewItem] = useState({
        meal_time: 'lunch', food_name: '', weight_g: '', ciqual_code: null, ciqual_data: null
    })
    // État pour l'IA
    const [isGenerating, setIsGenerating] = useState(false)
        useEffect(() => {
        fetchData()
        fetchTemplates()
        fetchFormTemplates()
    },[patientId])

    // --- HISTORIQUE & SPLIT SCREEN ---
    const [showHistoryPane, setShowHistoryPane] = useState(false)
    const [selectedHistoryNoteId, setSelectedHistoryNoteId] = useState('')
    const [historyNote, setHistoryNote] = useState(null)

    // --- MASQUE DE SAISIE INTERNE ---
    const [activeMask, setActiveMask] = useState(null)
    const [maskFields, setMaskFields] = useState([])
    const [maskAnswers, setMaskAnswers] = useState({})

    
    // Initialiser la note d'historique par défaut (la plus récente qui n'est pas celle en cours d'édition)
    /*
    useEffect(() => {
        const availableNotes = notes.filter(n => n.id !== editingNote?.id)
        if (availableNotes.length > 0 && !selectedHistoryNoteId) {
            setSelectedHistoryNoteId(availableNotes[0].id.toString())
            setHistoryNote(availableNotes[0])
        }
    }, [notes, editingNote]) // attention double brackets avec espace, retire le
    */

    // Gérer le changement dans le menu déroulant
    useEffect(() => {
        if (selectedHistoryNoteId) {
            setHistoryNote(notes.find(n => n.id.toString() === selectedHistoryNoteId))
        }
    }, [selectedHistoryNoteId, notes])
    
    // Synchronisation réactive de la note par défaut lors de l'ouverture du volet d'historique
    useEffect(() => {
        if (showHistoryPane && notes.length > 0) {
            const availableNotes = notes.filter(n => n.id !== editingNote?.id)
            if (availableNotes.length > 0) {
                const defaultNote = availableNotes[0]
                setSelectedHistoryNoteId(defaultNote.id.toString())
                setHistoryNote(defaultNote)
            } else {
                setSelectedHistoryNoteId('')
                setHistoryNote(null)
            }
        }
    }, [showHistoryPane, notes, editingNote])

    // --- CLÉ DE BROUILLON DYNAMIQUE ---
    const draftKey = editingNote ? `draft_edit_${editingNote.id}` : `draft_new_${patientId}`

    useEffect(() => {
        const action = searchParams.get('action')
        if (action === 'new_note' && !isModalOpen && templates.length >= 0) {
            handleOpenModal()
            setSearchParams({})
        }
        if (action === 'send_form' && !isSendModalOpen) {
            setIsSendModalOpen(true)
            setSearchParams({})
        }
    }, [searchParams, isModalOpen, isSendModalOpen, templates])

    useEffect(() => {
        if (autoOpenSendForm && !isSendModalOpen) {
            setIsSendModalOpen(true)
        }
    }, [autoOpenSendForm])

    const fetchTemplates = async () => {
        try { setTemplates((await api.get('/api/library/notes')).data) } catch (e) {}
    }

    const fetchFormTemplates = async () => {
        try { setFormTemplates((await api.get('/api/forms/templates')).data) } catch (e) {}
    }

    // On charge les notes ET les dernières mesures du patient d'un coup
    const fetchData = async () => {
        try {
            const[notesRes, measuresRes] = await Promise.all([
                api.get(`/api/notes/patient/${patientId}`),
                api.get(`/api/measurements/patient/${patientId}`)
            ])
            setNotes(notesRes.data)
            
            const m = measuresRes.data
            if (m.length > 0) {
                const last = m[m.length - 1]
                setLatestMeasures({ weight: last.weight, height: last.height })
            }
        } catch (error) {
            toast.error("Impossible de charger les données")
        } finally {
            setLoading(false)
        }
    }

    // Auto-sauvegarde de secours CONTINUE (Création ET Édition)
    useEffect(() => {
        if (isModalOpen) {
            if (formData.content) {
                localStorage.setItem(`${draftKey}_content`, formData.content);
            }
            if (recallItems.length > 0) {
                localStorage.setItem(`${draftKey}_recall`, JSON.stringify(recallItems));
            } else {
                localStorage.removeItem(`${draftKey}_recall`);
            }
        }
    }, [formData.content, recallItems, isModalOpen, draftKey]);

    const handleOpenModal = (note = null) => {
        const currentDraftKey = note ? `draft_edit_${note.id}` : `draft_new_${patientId}`
        const backupText = localStorage.getItem(`${currentDraftKey}_content`);
        const backupRecall = localStorage.getItem(`${currentDraftKey}_recall`);

        if (note) {
            setEditingNote(note)
            
            let initialContent = note.content;
            let initialRecall = note.diet_recall ? JSON.parse(note.diet_recall) : [];
            let initialRecallShow = !!note.diet_recall;

            // Restauration si un brouillon d'édition existe et diffère de la base
            if (backupText && backupText !== note.content && window.confirm("⚠️ Une modification non sauvegardée a été détectée pour cette note. Voulez-vous la restaurer ?")) {
                initialContent = backupText;
                if (backupRecall) {
                    initialRecall = JSON.parse(backupRecall);
                    initialRecallShow = true;
                }
            }

            // 🛡️ On s'assure de bien recharger le form_data existant pour ne pas l'écraser !
            setFormData({ date: note.date, note_type: note.note_type, content: initialContent, diet_recall: note.diet_recall, form_data: note.form_data || null })
            setRecallItems(initialRecall);
            setShowDietRecall(initialRecallShow);
        } else {
            setEditingNote(null); 
            
            let initialContent = '';
            let initialRecall = [];
            let initialRecallShow = false;

            if ((backupText || backupRecall) && window.confirm("⚠️ Un brouillon de nouvelle note a été détecté. Voulez-vous le restaurer ?")) {
                if (backupText) initialContent = backupText;
                if (backupRecall) {
                    initialRecall = JSON.parse(backupRecall);
                    initialRecallShow = true;
                }
            }

            const aptId = searchParams.get('apt_id');
            setRecallItems(initialRecall); 
            setShowDietRecall(initialRecallShow);
            setFormData({ date: new Date().toISOString().split('T')[0], note_type: 'follow_up', content: initialContent, diet_recall: null, form_data: null, appointment_id: aptId || null })
        }
        
        // 🔄 CORRECTION BUG HISTORIQUE : On calcule la bonne note de droite au moment précis de l'ouverture
        const availableNotesForHistory = notes.filter(n => n.id !== (note ? note.id : null));
        if (availableNotesForHistory.length > 0) {
            setSelectedHistoryNoteId(availableNotesForHistory[0].id.toString());
            setHistoryNote(availableNotesForHistory[0]);
        } else {
            setSelectedHistoryNoteId('');
            setHistoryNote(null);
        }

        setShowHistoryPane(false) // On ferme l'historique par défaut
        setIsModalOpen(true)
    }



    const handleApplyTemplate = (templateContent) => {
        if (formData.content && !confirm("Remplacer le texte actuel par ce modèle ?")) return
        setFormData(prev => ({ ...prev, content: templateContent }))
    }

    const handleSubmit = async (e, redirectToPayment = false) => {
        e.preventDefault()
        try {
            const payload = { ...formData }
            if (showDietRecall && recallItems.length > 0) payload.diet_recall = JSON.stringify(recallItems)
            else payload.diet_recall = null

            if (editingNote) {
                await api.patch(`/api/notes/${editingNote.id}`, payload)
                toast.success("Note modifiée")
            } else {
                await api.post(`/api/notes/patient/${patientId}`, payload)
                toast.success("Consultation enregistrée")
            }
            // Nettoyage garanti
            localStorage.removeItem(`${draftKey}_content`)
            localStorage.removeItem(`${draftKey}_recall`)
            
            setIsModalOpen(false)
            fetchData()
            
            // Le tunnel "Fin de consultation"
            if (redirectToPayment && formData.appointment_id) {
                window.location.href = `/admin/appointments?action=open&apt_id=${formData.appointment_id}`
            }
        } catch (error) { toast.error("Erreur lors de la sauvegarde") }
    }


    const handleDelete = async (noteId) => {
        if (!confirm("Supprimer cette note ?")) return
        try {
            await api.delete(`/api/notes/${noteId}`)
            toast.success("Supprimé")
            fetchData()
        } catch (error) { toast.error("Erreur") }
    }

    const handleSearchCiqual = async (text) => {
        setNewItem(prev => ({ ...prev, food_name: text, ciqual_code: null, ciqual_data: null }))
        if (text.length < 2) { setSuggestions([]); setShowSuggestions(false); return }
        try {
            const res = await api.get(`/api/ciqual/search?q=${encodeURIComponent(text)}`)
            setSuggestions(res.data)
            setShowSuggestions(true)
        } catch (e) { console.error(e) }
    }

    const selectCiqualItem = (item) => {
        setNewItem(prev => ({ ...prev, food_name: item.nom_fr, ciqual_code: item.code, ciqual_data: item }))
        setShowSuggestions(false)
    }

    const handleAddRecallItem = () => {
        if (!newItem.food_name || !newItem.weight_g) return toast.error("Aliment et poids (g) requis")
        const newItemObj = { ...newItem, id: Date.now(), weight_g: parseInt(newItem.weight_g) }
        setRecallItems([...recallItems, newItemObj])
        setNewItem({ meal_time: newItem.meal_time, food_name: '', weight_g: '', ciqual_code: null, ciqual_data: null })
    }

    const handleRemoveRecallItem = (id) => {
        setRecallItems(recallItems.filter(i => i.id !== id))
    }

    // --- CALCULATEURS ---
    const calculateItemMacros = (item) => {
        if (!item.ciqual_data || !item.weight_g) return null
        const r = item.weight_g / 100
        return { kcal: item.ciqual_data.energie_kcal * r, prot: item.ciqual_data.proteines * r, gluc: item.ciqual_data.glucides * r, lip: item.ciqual_data.lipides * r }
    }

    const getDailyTotal = () => {
        let total = { kcal: 0, prot: 0, gluc: 0, lip: 0 }
        recallItems.forEach(item => {
            const m = calculateItemMacros(item)
            if (m) { total.kcal += m.kcal; total.prot += m.prot; total.gluc += m.gluc; total.lip += m.lip }
        })
        return total
    }

    // NOUVEAU : CALCULATEUR DES BESOINS THÉORIQUES (Mifflin-St Jeor)
    const getTheoreticalNeeds = () => {
        if (!patient?.gender || !patient?.birth_date || !latestMeasures.weight || !latestMeasures.height) return null;
        
        const age = differenceInYears(new Date(), new Date(patient.birth_date));
        const w = parseFloat(latestMeasures.weight);
        const h = parseFloat(latestMeasures.height);
        
        let bmr = 0;
        // Formule de Mifflin-St Jeor
        if (patient.gender === 'M') {
            bmr = (10 * w) + (6.25 * h) - (5 * age) + 5;
        } else {
            bmr = (10 * w) + (6.25 * h) - (5 * age) - 161;
        }
        
        // Dépense Énergétique Journalière (TDEE)
        const tdee = bmr * activityLevel;
        
        // Répartition classique: 15% Prot (4 kcal/g), 50% Gluc (4 kcal/g), 35% Lip (9 kcal/g)
        return {
            kcal: tdee,
            prot: (tdee * 0.15) / 4,
            gluc: (tdee * 0.50) / 4,
            lip: (tdee * 0.35) / 9
        }
    }
    const theoreticalNeeds = getTheoreticalNeeds()

    const toggleDietRecall = () => {
        if (showDietRecall && recallItems.length > 0) {
            if (!confirm("Fermer l'enquête effacera les aliments saisis. Continuer ?")) return
            setRecallItems([])
        }
        setShowDietRecall(!showDietRecall)
        if (!showDietRecall) setShowHistoryPane(false) // Ferme l'historique si on ouvre le carnet
    }

    const toggleHistoryPane = () => {
        setShowHistoryPane(!showHistoryPane)
        if (!showHistoryPane && showDietRecall) {
            if (recallItems.length > 0 && !confirm("Ouvrir l'historique fermera et effacera l'enquête alimentaire en cours. Continuer ?")) return;
            setRecallItems([])
            setShowDietRecall(false)
        }
    }


    const handleSend = async () => {
        if (!sendVia.email && !sendVia.sms) return toast.error("Sélectionnez au moins un canal d'envoi.")
        setIsSending(true)
        const toastId = toast.loading("Envoi en cours...")
        let errors =[]
        try {
            if (sendVia.email) { try { await api.post(`/api/forms/requests/${generatedReqId}/send-email`) } catch (e) { errors.push("Email") } }
            if (sendVia.sms)   { try { await api.post(`/api/forms/requests/${generatedReqId}/send-sms`) } catch (e) { errors.push("SMS") } }
            if (errors.length === 0) {
                toast.success(sendVia.email && sendVia.sms ? "Email et SMS envoyés !" : sendVia.email ? "Email envoyé !" : "SMS envoyé !", { id: toastId })
                setIsSendModalOpen(false)
            } else { toast.error(`Échec : ${errors.join(" et ")}`, { id: toastId }) }
        } finally { setIsSending(false) }
    }

    const handleCopyLink = () => {
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(generatedLink)
        } else {
            const textArea = document.createElement("textarea"); textArea.value = generatedLink; document.body.appendChild(textArea); textArea.select(); document.execCommand("copy"); textArea.remove()
        }
        toast.success("Lien copié !")
    }

    if (loading) return <div className="p-8 text-center"><div className="spinner"/></div>

    // --- MAGIE : CONVERSION EN PLAN ALIMENTAIRE ---
    const handleConvertToDietPlan = async () => {
        if (recallItems.length === 0) return toast.error("L'enquête alimentaire est vide !");
        
        const toastId = toast.loading("Création du plan en cours...");
        try {
            // 1. Créer un nouveau plan vide au nom du patient
            const planRes = await api.post('/api/diet-plans/plans', {
                patient_id: patientId,
                name: `Enquête du ${format(new Date(), 'dd/MM/yyyy')}`,
                notes: "Généré automatiquement depuis le rappel des 24h."
            });
            const newPlanId = planRes.data.id;

            // 2. Transférer chaque aliment de l'enquête vers ce nouveau plan
            const promises = recallItems.map((item, index) => {
                return api.post(`/api/diet-plans/plans/${newPlanId}/items`, {
                    meal_time: item.meal_time,
                    food_name: item.food_name,
                    quantity: item.quantity || '',
                    notes: item.notes || '',
                    weight_g: item.weight_g,
                    ciqual_code: item.ciqual_code,
                    sort_order: index
                });
            });
            
            await Promise.all(promises);

            toast.success("Plan alimentaire généré avec succès ! Allez dans l'onglet 'Plan Alimentaire' pour le finaliser.", { id: toastId, duration: 6000 });
        } catch (error) {
            toast.error("Erreur lors de la génération du plan.", { id: toastId });
        }
    }

    // --- MAGIE IA : GASTON EN STREAMING ---
    const handleGenerateAISummary = async () => {
        setIsGenerating(true)
        const toastId = toast.loading("Gaston réfléchit...")
        
        // On prépare le terrain dans l'éditeur de texte
        const currentText = formData.content ? formData.content + "\n\n" : ""
        let newContent = currentText + "✨ --- SYNTHÈSE PRÉ-SÉANCE (IA) ---\n"
        setFormData(prev => ({ ...prev, content: newContent }))

        try {
            const token = localStorage.getItem('token')
            
            // On utilise fetch nativement pour pouvoir lire le flux (stream)
            const response = await fetch(`/api/ai/patient/${patientId}/summary`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.detail || "Erreur serveur");
            }

            toast.success("Génération en cours...", { id: toastId })


            // Lecture du flux de données
            const reader = response.body.getReader()
            const decoder = new TextDecoder("utf-8")
            let done = false

            while (!done) {
                const { value, done: readerDone } = await reader.read()
                done = readerDone
                if (value) {
                    const chunk = decoder.decode(value, { stream: true })
                    newContent += chunk
                    // On met à jour l'écran avec chaque nouveau mot !
                    setFormData(prev => ({ ...prev, content: newContent }))
                }
            }

            // Fin de la génération, on referme le bloc
            setFormData(prev => ({ ...prev, content: newContent + "\n----------------------------------\n\n" }))
            
        } catch (error) {
            toast.error(error.message || "Gaston est indisponible.", { id: toastId })
        } finally {
            setIsGenerating(false)
        }
    }

    const handlePrintNote = (note) => {
        const printWindow = window.open('', '_blank');
        const dateStr = format(new Date(note.date), 'dd/MM/yyyy');
        const typeLabel = NOTE_TYPES[note.note_type]?.label || note.note_type;
        
        let recallHtml = '';
        if (note.diet_recall) {
            try {
                const items = JSON.parse(note.diet_recall);
                if (items.length > 0) {
                    const totalKcal = Math.round(items.reduce((acc, item) => acc + ((item.ciqual_data?.energie_kcal || 0) * (item.weight_g/100)), 0));
                    recallHtml = `
                        <div style="margin-top: 20px; padding: 15px; border: 1px solid #ddd; background: #fafafa;">
                            <h4 style="margin: 0 0 10px 0;">Enquête alimentaire jointe (Total : ${totalKcal} kcal)</h4>
                            <ul style="margin: 0; padding-left: 20px;">
                                ${items.map(i => `<li>${i.food_name} (${i.weight_g}g)</li>`).join('')}
                            </ul>
                        </div>
                    `;
                }
            } catch(e) {}
        }

        printWindow.document.write(`
            <html>
                <head>
                    <title>Note - ${patient.first_name} ${patient.last_name} - ${dateStr}</title>
                    <style>
                        body { font-family: sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 40px auto; }
                        .header { border-bottom: 2px solid #7d9668; padding-bottom: 10px; margin-bottom: 20px; }
                        h1 { margin: 0; color: #7d9668; }
                        .meta { color: #666; font-style: italic; }
                        .content { white-space: pre-wrap; font-size: 14px; }
                        @media print { body { margin: 0; } }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>${patient.first_name} ${patient.last_name}</h1>
                        <div class="meta">Consultation du ${dateStr} - ${typeLabel}</div>
                    </div>
                    <div class="content">${note.content}</div>
                    ${recallHtml}
                </body>
            </html>
        `);
        printWindow.document.close();
        setTimeout(() => {
            printWindow.print();
        }, 500); // Petit délai pour être sûr que le CSS est chargé
    }

    const handleInjectMask = async () => {
        const startTag = `=== 📝 MASQUE: ${activeMask.name.toUpperCase()} (#${activeMask.id}) ===`;
        const endTag = `=== FIN MASQUE (#${activeMask.id}) ===`;
        
        let text = `${startTag}\n`;
        let weightToSave = null;
        let heightToSave = null;

        maskFields.forEach(f => {
            const ans = maskAnswers[f.id];
            if (!ans || (Array.isArray(ans) && ans.length === 0) || (f.type === 'repeater' && ans.filter(Boolean).length === 0)) return; // On ignore les champs vides
            
            let displayAns = ans;
            if (Array.isArray(ans)) {
                // Si c'est un répéteur, on fait une belle liste à puces textuelle
                displayAns = f.type === 'repeater' ? '\n  - ' + ans.filter(Boolean).join('\n  - ') : ans.join(', ');
            } else if (f.type === 'richtext') {
                // On nettoie les balises HTML pour la note textuelle (Laëtitia verra du texte propre)
                // Mais la vraie réponse HTML reste dans le maskAnswers pour les futurs PDF !
                displayAns = '\n' + ans.replace(/<[^>]*>?/gm, ''); 
            }
            
            text += `\n🔸 ${f.label}\n   ${displayAns}\n`;

            // Interception des constantes système !
            if (f.type === 'sys_weight') weightToSave = parseFloat(ans);
            if (f.type === 'sys_height') heightToSave = parseFloat(ans);
        });
        text += `\n${endTag}`;

        // 1. On injecte dans l'éditeur de texte ET on FUSIONNE le JSON brut pour les futurs courriers !
        setFormData(prev => {
            let existingData = { }; // attention espace
            try { 
                existingData = JSON.parse(prev.form_data || '{ }'); // attention espace
            } catch(e) {}
            
            const mergedData = { ...existingData, ...maskAnswers };
            
            let newContent = prev.content || "";
            const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`${escapeRegExp(startTag)}[\\s\\S]*?${escapeRegExp(endTag)}`, 'g');

            if (regex.test(newContent)) {
                // Si le bloc de ce masque est déjà présent, on le remplace proprement
                newContent = newContent.replace(regex, text);
            } else {
                // Sinon, on l'ajoute à la fin comme d'habitude
                newContent = newContent ? newContent + '\n\n' + text : text;
            }
            
            return { 
                ...prev, 
                content: newContent,
                form_data: JSON.stringify(mergedData)
            };
        });

        // 2. On sauvegarde la biométrie en arrière-plan !
        if (weightToSave || heightToSave) {
            try {
                await api.post(`/api/measurements/patient/${patientId}`, {
                    date: formData.date,
                    weight: weightToSave || undefined,
                    height: heightToSave || undefined
                });
                toast.success("Mesures ajoutées à la courbe biométrique !");
                setLatestMeasures(prev => ({ weight: weightToSave || prev.weight, height: heightToSave || prev.height }));
            } catch(e) {
                toast.error("Erreur sauvegarde des mesures");
            }
        }

        // On referme le masque pour la laisser peaufiner le texte
        setActiveMask(null);
    }

    return (
        <div className="space-y-6">
           <div className="flex justify-between items-center">
                <h3 className="text-lg font-serif text-sage-700">Dossier de suivi</h3>
                <div className="flex gap-2">
                    <button
                        onClick={() => { setGeneratedLink(''); setSelectedFormId(''); setSendVia({ email: true, sms: false }); setIsSendModalOpen(true) }}
                        className="btn bg-orange-100 text-orange-700 hover:bg-orange-200 flex items-center gap-2 py-2 text-sm shadow-sm transition-colors"
                    >
                        <Send className="w-4 h-4" /> Transmettre un formulaire
                    </button>
                    <button onClick={() => handleOpenModal()} className="btn btn-primary flex items-center gap-2 py-2 text-sm shadow-sm">
                        <Plus className="w-4 h-4" /> Nouvelle Note
                    </button>
                </div>
            </div>

            {/* LISTE DES NOTES */}
            <div className="space-y-4">
                {notes.length === 0 ? (
                    <div className="text-center p-8 border-2 border-dashed border-gray-100 rounded-xl bg-gray-50 text-gray-400">
                        <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>Aucune consultation enregistrée.</p>
                    </div>
                ) : (
                    notes.map(note => {
                        let parsedRecall = null
                        if (note.diet_recall) {
                            try { parsedRecall = JSON.parse(note.diet_recall) } catch(e){}
                        }

                        return (
                        <div key={note.id} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all group">
                            <div className="flex justify-between items-start mb-3 border-b border-gray-100 pb-3">
                                <div className="flex items-center gap-3">
                                    <div className="flex flex-col items-center bg-gray-50 px-3 py-1 rounded border border-gray-200">
                                        <span className="text-xs font-bold text-gray-500 uppercase">{format(new Date(note.date), 'MMM', { locale: fr })}</span>
                                        <span className="text-lg font-bold text-gray-700">{format(new Date(note.date), 'dd')}</span>
                                    </div>
                                    <div>
                                        <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider ${NOTE_TYPES[note.note_type]?.color || 'bg-gray-100'}`}>
                                            {NOTE_TYPES[note.note_type]?.label || note.note_type}
                                        </span>
                                        <p className="text-xs text-gray-400 mt-1">Créé le {format(new Date(note.created_at), 'dd/MM à HH:mm')}</p>
                                    </div>
                                </div>
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => handlePrintNote(note)} className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-800 rounded" title="Imprimer"><Printer className="w-4 h-4" /></button>
                                    <button onClick={() => handleOpenModal(note)} className="p-2 hover:bg-sage-50 text-gray-400 hover:text-sage-600 rounded"><Edit2 className="w-4 h-4" /></button>
                                    <button onClick={() => setShareItem({ type: 'Compte-rendu', id: note.id, title: note.title })} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-white rounded border border-transparent hover:border-blue-200 transition-all" title="Partager au réseau">
                                        <Link className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => handleDelete(note.id)} className="p-2 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded"><Trash2 className="w-4 h-4" /></button>
                                </div>
                            </div>

                            {/* LOUPE POUR LE RAPPEL 24H */}
                            {parsedRecall && parsedRecall.length > 0 && (
                                <div className="mb-4">
                                    <div 
                                        onClick={() => setExpandedRecallId(expandedRecallId === note.id ? null : note.id)}
                                        className="bg-orange-50/50 hover:bg-orange-100/50 border border-orange-100 rounded-lg p-3 flex items-center gap-3 w-fit cursor-pointer transition-colors"
                                    >
                                        <BarChart3 className="w-5 h-5 text-orange-500" />
                                        <div>
                                            <p className="text-xs font-bold text-orange-800 uppercase">Enquête alimentaire jointe</p>
                                            <p className="text-sm font-medium text-orange-700">{parsedRecall.length} aliments • Total : {Math.round(parsedRecall.reduce((acc, item) => acc + ((item.ciqual_data?.energie_kcal || 0) * (item.weight_g/100)), 0))} kcal</p>
                                        </div>
                                        <div className="ml-2 pl-2 border-l border-orange-200 text-orange-400">
                                            {expandedRecallId === note.id ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
                                        </div>
                                    </div>
                                    
                                    {expandedRecallId === note.id && (
                                        <div className="mt-2 ml-4 p-3 bg-white border border-orange-100 rounded-lg shadow-sm text-sm animate-in slide-in-from-top-2">
                                            <ul className="space-y-1.5">
                                                {parsedRecall.map(item => {
                                                    const kcal = item.ciqual_data ? Math.round((item.ciqual_data.energie_kcal * item.weight_g) / 100) : 0
                                                    return (
                                                        <li key={item.id} className="flex items-center gap-2 text-gray-700">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-orange-300"></div>
                                                            <span className="font-semibold">{item.food_name}</span> 
                                                            <span className="text-gray-400">({item.weight_g}g)</span>
                                                            <span className="ml-auto text-xs font-bold text-orange-600 bg-orange-50 px-1.5 rounded">{kcal} kcal</span>
                                                        </li>
                                                    )
                                                })}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="text-gray-700 whitespace-pre-wrap font-sans text-sm leading-relaxed">
                                {note.content}
                            </div>
                        </div>
                    )})
                )}
            </div>

            {/* MODALE SPLIT-SCREEN NOTE */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className={`bg-white rounded-2xl shadow-2xl flex flex-col max-h-[95vh] transition-all duration-300 ${(showDietRecall || showHistoryPane) ? 'w-full max-w-[1400px]' : 'w-full max-w-5xl'}`}>
                        {/* Header Modale */}
                        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-2xl shrink-0">
                            <div className="flex items-center gap-4">
                                <h3 className="font-serif font-bold text-xl text-gray-800 flex items-center gap-2">
                                    {editingNote ? 'Modifier la consultation' : 'Dossier patient : Consultation'}
                                </h3>
                                
                                <div className="flex bg-white rounded-lg border border-gray-200 p-1 shadow-sm">
                                    <button
                                        type="button"
                                        onClick={toggleHistoryPane}
                                        className={`px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-2 transition-colors ${
                                            showHistoryPane ? 'bg-sage-600 text-white' : 'text-sage-600 hover:bg-sage-50'
                                        }`}
                                    >
                                        <Eye className="w-4 h-4" /> Historique
                                    </button>
                                    <button
                                        type="button"
                                        onClick={toggleDietRecall}
                                        className={`px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-2 transition-colors ${
                                            showDietRecall ? 'bg-orange-500 text-white' : 'text-orange-600 hover:bg-orange-50'
                                        }`}
                                    >
                                        <BarChart3 className="w-4 h-4" /> Enquête Alim.
                                    </button>
                                </div>
                            </div>
                            <button onClick={() => { setIsModalOpen(false); localStorage.removeItem(`${draftKey}_content`); localStorage.removeItem(`${draftKey}_recall`); }} className="text-gray-400 hover:text-gray-600 p-1"><X className="w-6 h-6" /></button>
                        </div>

                        {/* Corps Split Screen */}
                        <div className="flex-1 flex min-h-0">

                            {/* COLONNE GAUCHE : NOTE TEXTE */}
                            <div className={`flex flex-col p-6 overflow-y-auto ${showDietRecall || showHistoryPane ? 'w-1/2 lg:w-[65%] border-r border-gray-200 bg-white' : 'w-full bg-white'}`}>
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Date</label>
                                        <input type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} className="w-full text-sm py-2" />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Type de consultation</label>
                                        <select value={formData.note_type} onChange={e => setFormData({...formData, note_type: e.target.value})} className="w-full text-sm py-2 bg-gray-50">
                                            <option value="follow_up">Suivi classique</option>
                                            <option value="anamnese">Bilan Initial (Anamnèse)</option>
                                            <option value="free">Appel / Note libre</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Templates Rapides & Assistant IA */}
                                <div className="flex flex-wrap gap-2 items-center bg-blue-50/50 p-2.5 rounded-xl border border-blue-100 mb-4">
                                    <span className="text-[10px] font-bold text-blue-800 uppercase px-1">Trames :</span>
                                    {templates.filter(t => t.note_type === formData.note_type).map(tpl => (
                                        <button key={tpl.id} type="button" onClick={() => handleApplyTemplate(tpl.content)} className="text-xs bg-white text-blue-700 px-3 py-1 rounded-lg border border-blue-200 hover:bg-blue-100 shadow-sm transition-all">
                                            {tpl.name}
                                        </button>
                                    ))}

                                    <div className="border-l border-blue-200 pl-3 ml-1 flex items-center gap-2">
                                        <span className="text-[10px] font-bold text-orange-800 uppercase px-1">Masques</span>
                                        <select 
                                            className="text-xs border-orange-200 text-orange-800 bg-orange-50 hover:bg-orange-100 rounded-lg py-1 px-2 cursor-pointer outline-none w-32"
                                            onChange={(e) => {
                                                if(!e.target.value) return;
                                                const targetId = parseInt(e.target.value);
                                                const form = formTemplates.find(f => f.id === targetId);
                                                setActiveMask(form);
                                                
                                                // On parse la structure
                                                let fields = [ ]; // attention espace
                                                try { fields = JSON.parse(form.fields_json || '[ ]'); } catch(err){ } // attention espace
                                                setMaskFields(fields);
                                                
                                                // Extraction et pré-remplissage des réponses existantes
                                                let existingAnswers = { }; // attention espace
                                                try {
                                                    existingAnswers = JSON.parse(formData.form_data || '{ }'); // attention espace
                                                } catch(err) { }
                                                
                                                const formAnswers = { }; // attention espace
                                                fields.forEach(f => {
                                                    if (existingAnswers[f.id] !== undefined) {
                                                        formAnswers[f.id] = existingAnswers[f.id];
                                                    }
                                                });
                                                setMaskAnswers(formAnswers);
                                                e.target.value = ''; // On reset
                                            }}
                                        >
                                            <option value="">Remplir...</option>
                                            {formTemplates.filter(t => t.is_internal).map(t => {
                                                let hasBeenFilled = false;
                                                try {
                                                    const usedKeys = Object.keys(JSON.parse(formData.form_data || '{ }')); // attention espace
                                                    const fields = JSON.parse(t.fields_json || '[ ]'); // attention espace
                                                    hasBeenFilled = fields.some(f => usedKeys.includes(f.id));
                                                } catch(e) { }
                                                
                                                return (
                                                    <option key={t.id} value={t.id}>
                                                        {hasBeenFilled ? '✏️ [Modifier] ' : '📝 '} {t.name}
                                                    </option>
                                                );
                                            })}
                                        </select>

                                    </div>
                                    
                                    {/* BOUTON GASTON (Aligné à droite grâce au ml-auto) */}
                                    <button 
                                        type="button" 
                                        onClick={handleGenerateAISummary}
                                        disabled={isGenerating}
                                        className="ml-auto text-xs bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-3 py-1.5 rounded-lg shadow-sm hover:shadow transition-all flex items-center gap-1.5 font-bold disabled:opacity-50"
                                    >
                                        {isGenerating ? <div className="spinner w-3 h-3 border-white" /> : <Sparkles className="w-3.5 h-3.5" />}
                                        Résumé IA (Gaston)
                                    </button>
                                </div>

                                {activeMask ? (
                                    <div className="flex-1 flex flex-col bg-gray-50 rounded-xl border border-gray-200 overflow-hidden animate-in zoom-in-95 mt-4">
                                        <div className="p-3 bg-orange-50 border-b border-orange-100 flex justify-between items-center">
                                            <span className="font-bold text-orange-800 text-sm">Saisie guidée : {activeMask.name}</span>
                                            <button type="button" onClick={() => setActiveMask(null)} className="text-gray-500 hover:text-red-500 bg-white p-1 rounded-md border border-gray-200"><X className="w-4 h-4"/></button>
                                        </div>
                                        <div className="p-4 flex-1 overflow-y-auto space-y-4 max-h-[50vh]">
                                            {maskFields.map(field => (
                                                <div key={field.id} className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
                                                    <label className="block text-xs font-bold text-gray-700 mb-2">{field.label}</label>
                                                    
                                                    {field.type === 'text' && <input className="w-full text-sm rounded-lg bg-gray-50 border-gray-200 focus:bg-white" value={maskAnswers[field.id] || ''} onChange={e => setMaskAnswers({...maskAnswers, [field.id]: e.target.value})} />}
                                                    {field.type === 'textarea' && <textarea className="w-full text-sm rounded-lg bg-gray-50 border-gray-200 focus:bg-white" rows="2" value={maskAnswers[field.id] || ''} onChange={e => setMaskAnswers({...maskAnswers, [field.id]: e.target.value})} />}
                                                    
                                                    {/* NOUVEAU : Éditeur Riche */}
                                                    {field.type === 'richtext' && (
                                                        <div className="bg-white">
                                                            <ReactQuill theme="snow" value={maskAnswers[field.id] || ''} onChange={val => setMaskAnswers({...maskAnswers, [field.id]: val})} modules={{ toolbar: [['bold', 'italic', 'underline'], [{'list': 'bullet'}], ['clean']] }}/>
                                                        </div>
                                                    )}
                                                    
                                                    {/* NOUVEAU : Répéteur Dynamique */}
                                                    {field.type === 'repeater' && (
                                                        <div className="space-y-2">
                                                            {(maskAnswers[field.id] || []).map((val, idx) => (
                                                                <div key={idx} className="flex items-center gap-2">
                                                                    <input className="w-full text-sm rounded-lg bg-gray-50 border-gray-200 focus:bg-white" value={val} onChange={e => { const arr = [...(maskAnswers[field.id] || [])]; arr[idx] = e.target.value; setMaskAnswers({...maskAnswers, [field.id]: arr}) }} placeholder="Nouvel élément..." />
                                                                    <button type="button" onClick={() => { const arr = (maskAnswers[field.id] || []).filter((_, i) => i !== idx); setMaskAnswers({...maskAnswers, [field.id]: arr}) }} className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1.5 rounded-lg"><X className="w-4 h-4"/></button>
                                                                </div>
                                                            ))}
                                                            <button type="button" onClick={() => { const arr = [...(maskAnswers[field.id] || []), '']; setMaskAnswers({...maskAnswers, [field.id]: arr}) }} className="text-xs text-sage-600 font-bold hover:bg-sage-50 px-3 py-1.5 rounded-lg border border-sage-200 flex items-center gap-1 w-fit"><Plus className="w-3 h-3"/> Ajouter une ligne</button>
                                                        </div>
                                                    )}

                                                    {(field.type === 'number' || field.type.startsWith('sys_')) && <input type="number" step="0.1" className="w-1/2 text-sm rounded-lg bg-gray-50 border-gray-200 focus:bg-white" value={maskAnswers[field.id] || ''} onChange={e => setMaskAnswers({...maskAnswers, [field.id]: e.target.value})} placeholder={field.type === 'sys_weight' ? 'kg' : field.type === 'sys_height' ? 'cm' : ''} />}
                                                    {field.type === 'radio' && field.options.split(',').map(o => o.trim()).map(opt => (
                                                        <label key={opt} className="flex items-center gap-2 text-sm mb-1 cursor-pointer hover:bg-gray-50 p-1 rounded"><input type="radio" className="text-sage-600 focus:ring-sage-500" checked={maskAnswers[field.id] === opt} onChange={() => setMaskAnswers({...maskAnswers, [field.id]: opt})}/> {opt}</label>
                                                    ))}
                                                    
                                                    {field.type === 'checkbox' && field.options.split(',').map(o => o.trim()).map(opt => {
                                                        const isChecked = (maskAnswers[field.id] || [ ]).includes(opt); // attention double brackets avec espace, retire le
                                                        return <label key={opt} className="flex items-center gap-2 text-sm mb-1 cursor-pointer hover:bg-gray-50 p-1 rounded"><input type="checkbox" className="text-sage-600 rounded focus:ring-sage-500" checked={isChecked} onChange={(e) => {
                                                            const arr = maskAnswers[field.id] || [ ]; // attention double brackets avec espace, retire le
                                                            setMaskAnswers({...maskAnswers, [field.id]: e.target.checked ? [...arr, opt] : arr.filter(x => x !== opt)});
                                                        }}/> {opt}</label>
                                                    })}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="p-3 bg-white border-t border-gray-200 shrink-0">
                                            <button type="button" onClick={handleInjectMask} className="w-full btn bg-orange-500 hover:bg-orange-600 text-white py-2.5 text-sm shadow-md flex items-center justify-center gap-2">
                                                Injecter dans la consultation
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <textarea
                                        value={formData.content}
                                        onChange={e => setFormData({...formData, content: e.target.value})}
                                        className="w-full flex-1 min-h-[400px] font-sans text-sm leading-relaxed p-4 bg-gray-50 border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sage-200 focus:border-sage-400 resize-none shadow-inner mt-4"
                                        placeholder="Rédigez l'histoire clinique, le ressenti, les objectifs ici..."
                                    />
                                )}
                            </div>


                            {/* COLONNE DROITE : CALCULATEUR (Visible si togglé) */}
                            {showDietRecall && (
                                <div className="w-[60%] flex flex-col bg-gray-50/50 animate-in slide-in-from-right-4">
                                    
                                    {/* DASHBOARD MACROS : COMPARATEUR THEORIQUE VS REEL */}
                                    <div className="grid grid-cols-2 divide-x divide-gray-200 border-b border-gray-200 bg-white">
                                        
                                        {/* BESOINS THÉORIQUES */}
                                        <div className="p-4 bg-blue-50/30">
                                            <div className="flex justify-between items-start mb-2">
                                                <h4 className="text-xs font-bold text-blue-800 uppercase flex items-center gap-1.5">
                                                    <Target className="w-4 h-4"/> Besoins (Objectifs)
                                                    
                                                    {/* INFO TOOLTIP */}
                                                    <div className="group relative flex items-center">
                                                        <Info className="w-3.5 h-3.5 text-blue-400 cursor-help" />
                                                        {/* On change 'bottom-full mb-2' par 'top-full mt-2' */}
                                                        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 hidden w-64 p-3 bg-gray-800 text-white text-[10px] rounded-lg shadow-xl group-hover:block z-50 normal-case font-normal leading-relaxed">
                                                            <p className="font-bold text-blue-300 mb-1">Algorithme BellaBlue</p>
                                                            Calcul basé sur la formule de <strong>Mifflin-St Jeor</strong> (Âge, Poids, Taille, Sexe) multipliée par le Niveau d'Activité Physique (NAP).
                                                            <div className="mt-2 pt-2 border-t border-gray-600">
                                                                <p className="font-bold text-gray-300 mb-1">Répartition par défaut :</p>
                                                                • Protéines : 15%<br/>
                                                                • Glucides : 50%<br/>
                                                                • Lipides : 35%
                                                            </div>
                                                            {/* Petite flèche en HAUT du tooltip (pointant vers le i) */}
                                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-800"></div>
                                                        </div>
                                                    </div>
                                                </h4>
                                                <select 
                                                    value={activityLevel} 
                                                    onChange={e => setActivityLevel(parseFloat(e.target.value))}
                                                    className="text-[10px] py-1 pl-2 pr-6 border-blue-200 bg-white text-blue-800 rounded font-bold"
                                                >
                                                    <option value={1.2}>Sédentaire</option>
                                                    <option value={1.375}>Légèrement actif</option>
                                                    <option value={1.55}>Modérément actif</option>
                                                    <option value={1.725}>Très actif</option>
                                                </select>
                                            </div>
                                            
                                            {theoreticalNeeds ? (
                                                <>
                                                    <div className="text-2xl font-black text-blue-600 mb-1">{Math.round(theoreticalNeeds.kcal)} <span className="text-xs font-bold text-blue-400 uppercase">kcal</span></div>
                                                    <div className="flex gap-2 text-[10px] font-bold">
                                                        <div className="text-blue-700 bg-blue-100 px-2 py-0.5 rounded">P: {Math.round(theoreticalNeeds.prot)}g</div>
                                                        <div className="text-blue-700 bg-blue-100 px-2 py-0.5 rounded">G: {Math.round(theoreticalNeeds.gluc)}g</div>
                                                        <div className="text-blue-700 bg-blue-100 px-2 py-0.5 rounded">L: {Math.round(theoreticalNeeds.lip)}g</div>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="text-xs text-blue-500 italic mt-2">
                                                    Âge, Sexe, Poids ou Taille manquants pour le calcul.
                                                </div>
                                            )}
                                        </div>

                                        {/* BILAN RÉEL (ENQUÊTE) */}
                                        <div className="p-4 bg-orange-50/30 relative">
                                            <h4 className="text-xs font-bold text-orange-800 uppercase mb-2 flex items-center gap-1.5">
                                                <Activity className="w-4 h-4"/> Bilan Enquête
                                            </h4>
                                            
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <div className="text-2xl font-black text-orange-600 mb-1">
                                                        {Math.round(getDailyTotal().kcal)} <span className="text-xs font-bold text-orange-400 uppercase">kcal</span>
                                                    </div>
                                                    <div className="flex gap-2 text-[10px] font-bold">
                                                        <div className="text-orange-700 bg-orange-100 px-2 py-0.5 rounded">P: {Math.round(getDailyTotal().prot)}g</div>
                                                        <div className="text-orange-700 bg-orange-100 px-2 py-0.5 rounded">G: {Math.round(getDailyTotal().gluc)}g</div>
                                                        <div className="text-orange-700 bg-orange-100 px-2 py-0.5 rounded">L: {Math.round(getDailyTotal().lip)}g</div>
                                                    </div>
                                                </div>

                                                {/* LE BOUTON MAGIQUE */}
                                                {recallItems.length > 0 && (
                                                    <button 
                                                        onClick={handleConvertToDietPlan}
                                                        className="flex flex-col items-center justify-center bg-white border border-orange-200 hover:bg-orange-100 hover:border-orange-400 text-orange-600 rounded-lg p-2 transition-all shadow-sm group"
                                                        title="Transformer cette enquête en Plan Alimentaire"
                                                    >
                                                        <Wand2 className="w-5 h-5 mb-1 group-hover:scale-110 transition-transform" />
                                                        <span className="text-[9px] font-bold uppercase tracking-wider">Créer Plan</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                    </div>

                                    {/* Barre d'ajout rapide */}
                                    <div className="p-4 bg-white border-b border-gray-200 shadow-sm z-10">
                                        <div className="flex gap-2">
                                            <select value={newItem.meal_time} onChange={e => setNewItem({...newItem, meal_time: e.target.value})} className="w-32 text-sm bg-gray-50 py-2">
                                                <option value="breakfast">Matin</option>
                                                <option value="lunch">Midi</option>
                                                <option value="snack">Goûter</option>
                                                <option value="dinner">Soir</option>
                                            </select>
                                            <div className="relative flex-1">
                                                <input 
                                                    placeholder="Rechercher aliment..." 
                                                    className="w-full text-sm py-2"
                                                    value={newItem.food_name}
                                                    onChange={e => handleSearchCiqual(e.target.value)}
                                                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                                />
                                                {showSuggestions && suggestions.length > 0 && (
                                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-2xl max-h-64 overflow-y-auto">
                                                        {suggestions.map((s) => (
                                                            <div key={s.code} onMouseDown={() => selectCiqualItem(s)} className="px-3 py-2.5 hover:bg-orange-50 cursor-pointer text-xs border-b border-gray-50 flex justify-between items-center">
                                                                <span className="font-bold text-gray-700">{s.nom_fr}</span>
                                                                <span className="text-orange-500 font-medium whitespace-nowrap ml-2">{Math.round(s.energie_kcal)} kcal/100g</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="relative w-24">
                                                <input type="number" placeholder="Poids" value={newItem.weight_g} onChange={e => setNewItem({...newItem, weight_g: e.target.value})} onKeyDown={e => e.key === 'Enter' && handleAddRecallItem()} className="w-full text-sm py-2 pr-6 font-bold text-right" />
                                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">g</span>
                                            </div>
                                            <button onClick={handleAddRecallItem} className="btn bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 text-sm shadow-sm"><ChevronRight className="w-4 h-4"/></button>
                                        </div>
                                    </div>

                                    {/* Liste des aliments groupés */}
                                    <div className="flex-1 overflow-y-auto p-4 space-y-6">
                                        {Object.entries(MEAL_TIMES).map(([mealKey, mealLabel]) => {
                                            const items = recallItems.filter(i => i.meal_time === mealKey)
                                            if(items.length === 0) return null;
                                            
                                            const mealKcal = items.reduce((acc, item) => acc + (calculateItemMacros(item)?.kcal || 0), 0)

                                            return (
                                                <div key={mealKey}>
                                                    <div className="flex justify-between items-end border-b border-gray-200 pb-1 mb-2">
                                                        <h5 className="font-bold text-gray-700 text-sm">{mealLabel}</h5>
                                                        <span className="text-xs font-bold text-gray-400">{Math.round(mealKcal)} kcal</span>
                                                    </div>
                                                    <div className="space-y-2">
                                                        {items.map(item => {
                                                            const macros = calculateItemMacros(item)
                                                            return (
                                                            <div key={item.id} className="bg-white border border-gray-100 p-2.5 rounded-lg flex justify-between items-center group shadow-sm">
                                                                <div className="flex-1">
                                                                    <p className="text-sm font-semibold text-gray-800 leading-tight">
                                                                        {item.food_name} <span className="text-gray-400 font-normal">({item.weight_g}g)</span>
                                                                    </p>
                                                                </div>
                                                                <div className="flex items-center gap-3">
                                                                    <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-1 rounded">
                                                                        {macros ? Math.round(macros.kcal) : 0} kcal
                                                                    </span>
                                                                    <button onClick={() => handleRemoveRecallItem(item.id)} className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1">
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )})}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* COLONNE DROITE : HISTORIQUE DES NOTES */}
                            {showHistoryPane && (
                                <div className="w-1/2 lg:w-[35%] flex flex-col bg-gray-50 animate-in slide-in-from-right-4">
                                    <div className="p-4 border-b border-gray-200 bg-white flex justify-between items-center shadow-sm z-10">
                                        <div className="flex-1 max-w-md">
                                            <label className="text-[10px] font-bold text-gray-500 uppercase mb-1 block">Consulter une ancienne note :</label>
                                            <select 
                                                value={selectedHistoryNoteId} 
                                                onChange={e => setSelectedHistoryNoteId(e.target.value)}
                                                className="w-full text-sm font-bold text-sage-800 bg-sage-50 border-sage-200 focus:ring-sage-300"
                                            >
                                                {notes.filter(n => n.id !== editingNote?.id).map(n => (
                                                    <option key={n.id} value={n.id}>
                                                        {format(new Date(n.date), 'dd/MM/yyyy')} - {NOTE_TYPES[n.note_type]?.label || n.note_type}
                                                    </option>
                                                ))}
                                                {notes.filter(n => n.id !== editingNote?.id).length === 0 && <option value="">Aucun historique</option>}
                                            </select>
                                        </div>
                                        
                                        {/* Bouton Copy Forward */}
                                        {historyNote && (
                                            <button 
                                                type="button"
                                                onClick={() => {
                                                    if (formData.content && !confirm("Remplacer votre texte actuel par le contenu de cette ancienne note ?")) return;
                                                    setFormData(prev => ({ ...prev, content: historyNote.content }));
                                                    toast.success("Texte copié !");
                                                }}
                                                className="btn bg-white border border-sage-200 text-sage-700 hover:bg-sage-50 shadow-sm ml-4 flex items-center gap-2"
                                            >
                                                <Copy className="w-4 h-4" /> Reprendre ce texte
                                            </button>
                                        )}
                                    </div>
                                    
                                    <div className="flex-1 overflow-y-auto p-6">
                                        {historyNote ? (
                                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                                                <div className="text-gray-800 whitespace-pre-wrap font-sans text-sm leading-relaxed">
                                                    {historyNote.content}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="h-full flex flex-col items-center justify-center text-gray-400">
                                                <FileText className="w-12 h-12 mb-3 opacity-20" />
                                                <p>Aucune note précédente à afficher.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                        </div>

                        {/* Footer Modale */}
                        <div className="p-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex justify-end gap-3 z-20 shrink-0">
                            <button type="button" onClick={() => { setIsModalOpen(false); localStorage.removeItem(`${draftKey}_content`); localStorage.removeItem(`${draftKey}_recall`); }} className="btn bg-white border border-gray-300 text-gray-600 hover:bg-gray-100 px-6">
                                Annuler
                            </button>
                            <button onClick={(e) => handleSubmit(e, false)} className="btn bg-sage-200 text-sage-800 hover:bg-sage-300 px-4">
                                Enregistrer
                            </button>
                            {formData.appointment_id && !editingNote && (
                                <button onClick={(e) => handleSubmit(e, true)} className="btn btn-primary px-6 shadow-lg flex items-center gap-2">
                                    <CreditCard className="w-4 h-4"/> Enregistrer & Encaisser
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
             {/* MODALE TRANSMISSION QUESTIONNAIRE */}
            {isSendModalOpen && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">

                        {/* Header */}
                        <div className="flex justify-between items-center p-6 border-b border-gray-100">
                            <h3 className="text-xl font-serif font-bold text-gray-800 flex items-center gap-2">
                                <Send className="w-5 h-5 text-orange-500" /> Transmettre un formulaire
                            </h3>
                            <button onClick={() => setIsSendModalOpen(false)}>
                                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                            </button>
                        </div>

                        <div className="p-6 space-y-5">

                            {/* RAPPEL PATIENT */}
                            {patient && (
                                <div className="bg-sage-50 border border-sage-100 rounded-xl p-4 flex items-center gap-4">
                                    <div className="w-10 h-10 bg-sage-200 rounded-full flex items-center justify-center text-sage-700 font-bold text-sm flex-shrink-0">
                                        {patient.first_name?.[0]}{patient.last_name?.[0]}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-bold text-gray-800 text-sm truncate">{patient.first_name} {patient.last_name}</p>
                                        <div className="flex flex-col sm:flex-row sm:gap-3 mt-0.5">
                                            {patient.email ? (
                                                <p className="text-xs text-gray-500 flex items-center gap-1 truncate">
                                                    <Mail className="w-3 h-3 flex-shrink-0" /> {patient.email}
                                                </p>
                                            ) : (
                                                <p className="text-xs text-red-400 flex items-center gap-1">
                                                    <Mail className="w-3 h-3" /> Pas d'email
                                                </p>
                                            )}
                                            {patient.phone ? (
                                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                                    <MessageSquare className="w-3 h-3 flex-shrink-0" /> {patient.phone}
                                                </p>
                                            ) : (
                                                <p className="text-xs text-red-400 flex items-center gap-1">
                                                    <MessageSquare className="w-3 h-3" /> Pas de téléphone
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* SÉLECTION DU FORMULAIRE */}
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Formulaire à envoyer</label>
                                <select
                                    value={selectedFormId}
                                    onChange={e => { setSelectedFormId(e.target.value); setGeneratedLink(''); setGeneratedReqId(null) }}
                                    className="w-full bg-gray-50 py-3 text-sm"
                                    disabled={!!generatedLink}
                                >
                                    <option value="">Sélectionnez un formulaire...</option>
                                    {formTemplates.filter(t => !t.is_internal).map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>

                            </div>

                            {/* LIEN GÉNÉRÉ */}
                            {!generatedLink ? (
                                <button
                                    disabled={!selectedFormId}
                                    onClick={async () => {
                                        try {
                                            const res = await api.post('/api/forms/requests', {
                                                patient_id: patientId,
                                                template_id: parseInt(selectedFormId)
                                            })
                                            setGeneratedLink(res.data.url)
                                            setGeneratedReqId(res.data.request_id)
                                        } catch(e) { toast.error("Erreur de génération du lien") }
                                    }}
                                    className="w-full btn btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Valider & Générer le lien
                                </button>
                            ) : (
                                <div className="space-y-5">
                                    {/* Lien avec bouton copier */}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2 flex items-center gap-1">
                                            <CheckCircle className="w-3.5 h-3.5 text-green-500" /> Lien généré
                                        </label>
                                        <div className="flex bg-green-50 border border-green-200 rounded-xl overflow-hidden">
                                            <input
                                                type="text"
                                                readOnly
                                                value={generatedLink}
                                                className="flex-1 text-xs text-gray-500 border-none bg-transparent px-3 py-2.5 min-w-0"
                                            />
                                            <button
                                                onClick={handleCopyLink}
                                                className="px-4 bg-green-100 hover:bg-green-200 text-green-700 transition-colors flex items-center gap-1.5 text-xs font-bold flex-shrink-0 border-l border-green-200"
                                                title="Copier le lien"
                                            >
                                                <Copy className="w-3.5 h-3.5" /> Copier
                                            </button>
                                        </div>
                                    </div>

                                    {/* CASES À COCHER CANAL D'ENVOI */}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-3">Envoyer via</label>
                                        <div className="grid grid-cols-2 gap-3">

                                            {/* Case Email */}
                                            <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all select-none ${
                                                sendVia.email
                                                    ? 'border-orange-300 bg-orange-50'
                                                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                                            } ${!patient?.email ? 'opacity-40 cursor-not-allowed' : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    className="hidden"
                                                    checked={sendVia.email}
                                                    disabled={!patient?.email}
                                                    onChange={e => setSendVia(prev => ({ ...prev, email: e.target.checked }))}
                                                />
                                                <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                                                    sendVia.email ? 'bg-orange-500' : 'bg-white border-2 border-gray-300'
                                                }`}>
                                                    {sendVia.email && <CheckCircle className="w-4 h-4 text-white" />}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-gray-700 flex items-center gap-1.5">
                                                        <Mail className="w-3.5 h-3.5" /> Email
                                                    </p>
                                                    <p className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[100px]">
                                                        {patient?.email || 'Non renseigné'}
                                                    </p>
                                                </div>
                                            </label>

                                            {/* Case SMS */}
                                            <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all select-none ${
                                                sendVia.sms
                                                    ? 'border-blue-300 bg-blue-50'
                                                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                                            } ${!patient?.phone ? 'opacity-40 cursor-not-allowed' : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    className="hidden"
                                                    checked={sendVia.sms}
                                                    disabled={!patient?.phone}
                                                    onChange={e => setSendVia(prev => ({ ...prev, sms: e.target.checked }))}
                                                />
                                                <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                                                    sendVia.sms ? 'bg-blue-500' : 'bg-white border-2 border-gray-300'
                                                }`}>
                                                    {sendVia.sms && <CheckCircle className="w-4 h-4 text-white" />}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-gray-700 flex items-center gap-1.5">
                                                        <MessageSquare className="w-3.5 h-3.5" /> SMS
                                                    </p>
                                                    <p className="text-[10px] text-gray-400 mt-0.5">
                                                        {patient?.phone || 'Non renseigné'}
                                                    </p>
                                                </div>
                                            </label>
                                        </div>

                                        {/* Avertissement si rien de coché */}
                                        {!sendVia.email && !sendVia.sms && (
                                            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                                                ⚠️ Sélectionnez au moins un canal pour envoyer.
                                            </p>
                                        )}
                                    </div>

                                    {/* BOUTON ENVOI UNIQUE */}
                                    <button
                                        onClick={handleSend}
                                        disabled={isSending || (!sendVia.email && !sendVia.sms)}
                                        className="w-full btn bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center gap-2 py-3 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {isSending ? (
                                            <span className="flex items-center gap-2"><div className="spinner w-4 h-4 border-white" /> Envoi en cours...</span>
                                        ) : (
                                            <>
                                                <Send className="w-4 h-4" />
                                                Envoyer
                                                {sendVia.email && sendVia.sms && ' (Email + SMS)'}
                                                {sendVia.email && !sendVia.sms && ' par Email'}
                                                {!sendVia.email && sendVia.sms && ' par SMS'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {shareItem && (
                <ShareModal 
                    itemType={shareItem.type} 
                    itemId={shareItem.id} 
                    itemTitle={shareItem.title} 
                    patient={patient} 
                    onClose={() => setShareItem(null)} 
                />
            )}
        </div>
    )
}